import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Info,
  Users,
  Database,
  Bot,
  Code,
  ExternalLink,
  Copy,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import { API_KEY, DATASET_ID, AGENT_CODES } from '../config/environment';
import type { Debtor } from '../types';

interface DemoInfoPageProps {
  debtor: Debtor | null;
}

interface DemoCredential {
  debtor_id: string;
  email: string;
  first_name: string;
  last_name: string;
  debtor_type: string;
  segment: string;
}

export default function DemoInfoPage({ debtor }: DemoInfoPageProps) {
  const [demoCredentials, setDemoCredentials] = useState<DemoCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    loadDemoCredentials();
  }, []);

  const loadDemoCredentials = async () => {
    setIsLoading(true);
    try {
      const result = await serenityClient.queryData<DemoCredential>(
        `SELECT debtor_id, email, first_name, last_name, debtor_type, segment FROM Debtors ORDER BY segment DESC, debtor_type LIMIT 10`
      );
      setDemoCredentials(result);
    } catch (error) {
      console.error('Failed to load demo credentials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, item: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const getSegmentColor = (segment: string) => {
    switch (segment) {
      case 'high':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={debtor ? "/dashboard" : "/login"}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <Info className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Demo Information</h1>
                <p className="text-xs text-gray-500">DFS Collections Portal - Serenity Star Demo</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Overview */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
            <h2 className="text-2xl font-bold mb-2">DFS Collections Portal</h2>
            <p className="text-blue-100 mb-4">
              This is a demonstration of a debt collection self-service portal powered by Serenity Star AI.
              Debtors can view their cases, make payments, chat with an AI assistant, upload documents,
              and submit hardship requests.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://serenitystar.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Serenity Star
              </a>
              <a
                href="https://api.serenitystar.ai/swagger"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              >
                <Code className="w-4 h-4" />
                API Docs
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Demo Credentials */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Demo Credentials</h2>
              </div>
              
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-2">
                  {demoCredentials.map((cred) => (
                    <div
                      key={cred.debtor_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {cred.debtor_type === 'corporate' ? cred.first_name : `${cred.first_name} ${cred.last_name}`}
                        </p>
                        <p className="text-sm text-gray-500">{cred.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getSegmentColor(cred.segment)}`}>
                          {cred.segment}
                        </span>
                        <button
                          onClick={() => copyToClipboard(cred.email, cred.email)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                          title="Copy email"
                        >
                          {copiedItem === cred.email ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-4">
                Password for all demo accounts: <code className="bg-gray-100 px-1 rounded">demo123</code>
              </p>
            </div>

            {/* Technical Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Technical Details</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Dataset ID</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded font-mono truncate">
                      {DATASET_ID}
                    </code>
                    <button
                      onClick={() => copyToClipboard(DATASET_ID, 'dataset')}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      {copiedItem === 'dataset' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500">API Key (Demo)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded font-mono truncate">
                      {API_KEY.substring(0, 8)}...{API_KEY.substring(API_KEY.length - 4)}
                    </code>
                    <button
                      onClick={() => copyToClipboard(API_KEY, 'apikey')}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      {copiedItem === 'apikey' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Tables</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Debtors', 'Cases', 'Documents', 'FileContents', 'PaymentPlans', 'Payments', 'Communications', 'ChatMessages', 'ExceptionRequests'].map((table) => (
                      <span key={table} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {table}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* AI Agents */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Bot className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">AI Agents</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(AGENT_CODES).map(([key, code]) => (
                  <div key={key} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{key.replace(/_/g, ' ')}</span>
                      <button
                        onClick={() => copyToClipboard(code, code)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                      >
                        {copiedItem === code ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <code className="text-xs text-gray-500 font-mono">{code}</code>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Portal Features</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">Dashboard</h3>
                  <p className="text-sm text-gray-500">View case summary, balances, and quick actions</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">Case Details</h3>
                  <p className="text-sm text-gray-500">Detailed view of debt, payments, and communications</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">Payments</h3>
                  <p className="text-sm text-gray-500">Make full/partial payments or set up plans</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">AI Chat</h3>
                  <p className="text-sm text-gray-500">24/7 AI assistant for questions and support</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">Documents</h3>
                  <p className="text-sm text-gray-500">Upload supporting documents securely</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1">Hardship</h3>
                  <p className="text-sm text-gray-500">Submit financial hardship requests</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
